import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef } from 'react';
import { useFocusTrap } from '../a11y/useFocusTrap';
import { ConsequenceNote } from '../notes/ConsequenceNote';
import { Button } from './Button';
import styles from './controls.module.css';
let dialogSeq = 0;
/**
 * The confirmation PH-UX-004 found missing everywhere: twelve destructive or
 * irreversible controls (`Disconnect`, "Delete permanently" ×2, a superuser
 * `Revoke`, …) fired on one click, with the consequence recorded only in a
 * code comment. `packages/ui-admin` had no confirm primitive at all — four
 * screens improvised `window.confirm` (which cannot be styled, is not
 * consistently testable, and says nothing about what is destroyed beyond the
 * browser's own generic prompt) and eight had no confirmation whatsoever.
 *
 * This is a plain controlled dialog, not a `<ConfirmAction>` that also owns a
 * trigger button — every call site already has its own trigger (a `Button` or
 * a bespoke one) with its own pending/disabled logic wired to its own
 * mutation, and duplicating that here would just be a second place for it to
 * drift from the first. The call site keeps its trigger, flips a boolean to
 * open this, and calls its OWN mutation from `onConfirm` — exactly as it
 * already calls it from the trigger today, just one step later.
 *
 * Accessibility, not left to each of the twelve sites to reinvent:
 *   - `role="alertdialog"` + `aria-modal`, labelled and described by its own
 *     content (never a bare "Are you sure?").
 *   - Focus moves onto Cancel when the dialog opens — never onto the
 *     destructive button, so a stray Enter cannot fire it. `useFocusTrap`
 *     traps Tab inside, and restores focus to whatever opened the dialog when
 *     it closes.
 *   - Escape cancels. Clicking the backdrop cancels (the same direction as
 *     Escape — never the destructive one).
 *   - Never `window.confirm`: this can state the specific consequence, is
 *     stylable, is keyboard- and screen-reader-navigable the same way every
 *     time, and is unit-testable without stubbing a browser API.
 */
export function ConfirmAction({ open, title, consequence, confirmLabel, pendingLabel, cancelLabel = 'Cancel', pending = false, onConfirm, onCancel, }) {
    const cancelRef = useRef(null);
    const dialogRef = useFocusTrap(open, {
        // The SAFE action gets initial focus, never the destructive one — a
        // keyboard user who reflexively hits Enter on open must cancel, not confirm.
        initialFocus: cancelRef,
        onEscape: onCancel,
        contain: true,
    });
    // One stable id pair for the component's lifetime, not regenerated per open,
    // so two instances mounted on one page (unlikely, but cheap to guarantee)
    // never collide on aria-labelledby/aria-describedby.
    const ids = useRef(null);
    if (ids.current == null) {
        dialogSeq += 1;
        ids.current = {
            title: `confirm-action-title-${dialogSeq}`,
            desc: `confirm-action-desc-${dialogSeq}`,
        };
    }
    if (!open)
        return null;
    return (_jsx("div", { className: styles.confirmBackdrop, role: "presentation", onClick: onCancel, children: _jsxs("div", { ref: dialogRef, className: styles.confirmDialog, role: "alertdialog", "aria-modal": "true", "aria-labelledby": ids.current.title, "aria-describedby": ids.current.desc, onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { id: ids.current.title, className: styles.confirmTitle, children: title }), _jsx("div", { id: ids.current.desc, children: _jsx(ConsequenceNote, { text: consequence }) }), _jsxs("div", { className: styles.confirmActions, children: [_jsx(Button, { ref: cancelRef, variant: "secondary", onClick: onCancel, disabled: pending, children: cancelLabel }), _jsx(Button, { variant: "destructive", onClick: onConfirm, disabled: pending, children: pending ? (pendingLabel ?? confirmLabel) : confirmLabel })] })] }) }));
}
//# sourceMappingURL=ConfirmAction.js.map